import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import { DateTime } from 'luxon'
import isEqual from 'lodash/isEqual'

import { useFulfillment, FULFILLMENT_TYPES } from '../FulfillmentProvider/FulfillmentProvider'
import Progress from '../Progress/Progress'
import Error from '../Error/Error'
import Radio from '../Form/Radio/Radio'
import Button from '../Button/Button'
import { dataByTypename } from '../../utils/apollo-helpers'
import { useDiningOptions, DINING_OPTION_BEHAVIORS } from '../../utils/dining-options'
import { Notification } from '../Notification/Notification'
import { isSameISODay, isSameISOTime, toRelativeDateString } from '../../utils/time-utils'
import Modal from '../Modal/Modal'
import { useEventOnMount } from '../ReactGAUtils/ReactGAUtils'

import './FulfillmentSelectorModal.css'
import { FulfillmentConfirm } from './FulfillmentConfirm/FulfillmentConfirm'
import { FulfillmentHeader } from './FulfillmentHeader/FulfillmentHeader'
import { useGetCart } from '../CartQuery/CartQuery'
import { FlagOn, FlagOff } from '../FeatureFlag/FeatureFlag'
import DeliveryFulfillmentSelectorModal from './DeliveryFulfillmentSelectorModal'

const WrappedModal = ({children, ...props}) =>
  <Modal
    responsive={false}
    wrapper={children => <Form>{children}</Form>}
    {...props}
  >
    <div styleName='body'>
      {children}
    </div>
  </Modal>

// TODO: Deprecate this definition or remove this code in favor of fulfillment-helpers
const getDatesAndTimesFromResponse = (data, selectedDate) => {
  const dates = data.diningOptions
    .find(option => option.behavior === DINING_OPTION_BEHAVIORS.TAKE_OUT)
    .futureSchedule.dates
    .filter(({timesAndGaps = []}) => timesAndGaps.length >= 1)
  const dateList = dates.map(d => d.date)
  let currentDate = dates[0]
  if (selectedDate) {
    const matchingDate = dates.find(d => isSameISODay(d.date, selectedDate))
    if (matchingDate) {
      currentDate = matchingDate
    }
  }
  const timeList = currentDate.timesAndGaps
  return { dateList, timeList, currentDate }
}

// Convert date/time from ISO 8601 format into display (human readable) format
const formatTime = (time) => {
  const dateTime = DateTime.fromISO(time)
  return dateTime.toLocaleString(DateTime.TIME_SIMPLE)
}

const FulfillmentSelectorModalInternal = ({ minimumTakeoutTime, onClose }) => {
  const { fulfillmentType, fulfillmentTime, setFulfillment, selected, valid } = useFulfillment()
  const { cart, loading: cartLoading } = useGetCart()
  const [showConfirm, setShowConfirm] = useState(false)
  const [updateFulfillmentError, setUpdateFulfillmentError] = useState(undefined)
  const numberOfSelections = cart ? cart.order.numberOfSelections : 0
  const cartHasItems = numberOfSelections > 0

  let fulfillmentDate
  if (fulfillmentTime) {
    fulfillmentDate = DateTime.fromISO(fulfillmentTime).toISODate()
  }
  const { loading, data, refetch, error: diningOptionsError } = useDiningOptions({ networkOnly: true })

  useEventOnMount({
    category: 'Cart',
    action: 'Set Fulfillment',
    label: selected ? 'Update' : 'Initial'
  })

  if (loading || cartLoading) {
    return <WrappedModal onClose={onClose}>
      <Progress eventData={{ category: 'Fulfillment Modal' }} />
    </WrappedModal>
  }

  const error = diningOptionsError || updateFulfillmentError
  const retry = diningOptionsError ? refetch : undefined

  if (error) {
    return <WrappedModal onClose={onClose}>
      <Error error={error} retry={retry} />
    </WrappedModal>
  }

  const { timeList: initialTimes, currentDate } = getDatesAndTimesFromResponse(data, fulfillmentDate)
  let currentTime = initialTimes[0]
  if (fulfillmentTime) {
    const matchingTime = initialTimes.find(timeSlot => isSameISOTime(timeSlot.time, fulfillmentTime))
    if (matchingTime) {
      currentTime = matchingTime
    }
  }

  let initialFulfillmentType = fulfillmentType || FULFILLMENT_TYPES.ASAP
  const takeoutDiningOption = data.diningOptions.find(option => option.behavior === 'TAKE_OUT')
  let asapAvailable = true
  if (takeoutDiningOption && !takeoutDiningOption.asapSchedule.availableNow) {
    asapAvailable = false
    initialFulfillmentType = FULFILLMENT_TYPES.FUTURE
  }

  const initialValues = {
    fulfillmentType: initialFulfillmentType,
    fulfillmentDate: currentDate ? currentDate.date : undefined,
    fulfillmentTime: currentTime ? currentTime.time : undefined
  }

  const handleSubmit = async (values) => {
    const dirty = !isEqual(values, initialValues)
    const needsConfirm = dirty && cartHasItems && !showConfirm

    if (needsConfirm) {
      return setShowConfirm(true)
    }

    try {
      await setFulfillment({
        fulfillmentType: values.fulfillmentType,
        fulfillmentTime: values.fulfillmentTime
      })
      onClose()
    } catch (error) {
      setUpdateFulfillmentError(error)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, setFieldValue, handleSubmit, isSubmitting }) => {
        const selectedDate = values.fulfillmentDate
        const { dateList, timeList } = getDatesAndTimesFromResponse(data, selectedDate)
        if (showConfirm) {
          return (
            <WrappedModal
              header={<h2 styleName='header'>Are you sure?</h2>}
            >
              <FulfillmentConfirm onClose={onClose} />
            </WrappedModal>
          )
        }

        return (
          <WrappedModal
            handleSubmit={handleSubmit}
            header={<FulfillmentHeader editing={selected} />}
            footer={
              <Button data-testid='fulfillment-selector-submit' type='submit' style='primary' loading={isSubmitting}>
                {selected ? 'Update' : 'Start Order'}
              </Button>
            }
            onClose={(selected && onClose) || undefined}
          >
            <div>
              {!valid &&
                <Notification severity='warning'>
                  Your order time is no longer available so your cart has been cleared. Select a new time to begin a new order.
                </Notification>
              }
              {!asapAvailable &&
                <Notification>
                  You can only place scheduled orders right now
                </Notification>
              }
              {selected && cartHasItems && valid &&
              <Notification>
                  Changing your order time will remove all items you currently have in your cart
              </Notification>
              }
              <h2 styleName='when'>When would you like your order:</h2>
              <Field
                id='fulfillment_type_asap'
                name='fulfillmentType'
                value={FULFILLMENT_TYPES.ASAP}
                component={Radio}
                disabled={!asapAvailable}
                label={
                  <div>
                    <span>Order now</span>
                    {values.fulfillmentType === FULFILLMENT_TYPES.ASAP && (
                      <p id='prep_time_message' styleName='promise'>
                      Ready in about <span styleName='time'>{minimumTakeoutTime}-{minimumTakeoutTime + 5} minutes</span>
                      </p>
                    )}
                  </div>
                }
              />
              <Field
                id='fulfillment_type_future'
                name='fulfillmentType'
                value={FULFILLMENT_TYPES.FUTURE}
                component={Radio}
                label={<span>Schedule for later</span>}
              />
              {values.fulfillmentType === FULFILLMENT_TYPES.FUTURE &&
                <div data-testid='fulfillment-selectors' id='fulfillment_selectors' styleName='fulfillment_time_selectors'>
                  <select
                    data-testid='fulfillment-selector-dates'
                    styleName='select'
                    id='fulfillment_time_date'
                    name='fulfillmentDate'
                    value={values.fulfillmentDate}
                    onChange={(event) => {
                      // Select same time on different day, or first time if selected is not available
                      const { timeList: newTimes } = getDatesAndTimesFromResponse(data, event.target.value)
                      if (!newTimes.some(timeSlot => timeSlot.time === values.fulfillmentTime)) {
                        let matchingTime = newTimes.find(timeSlot => isSameISOTime(timeSlot.time, values.fulfillmentTime))
                        if (!matchingTime) {
                          matchingTime = newTimes[0]
                        }
                        setFieldValue('fulfillmentTime', matchingTime.time)
                      }
                      handleChange(event)
                    }}
                  >
                    {dateList.map(date => (
                      <option data-testid='fulfillment-selector-date' value={date} key={date}>
                        {toRelativeDateString(date)}
                      </option>
                    ))}
                  </select>
                  <select
                    data-testid='fulfillment-selector-times'
                    styleName='select'
                    id='fulfillment_time'
                    name='fulfillmentTime'
                    value={values.fulfillmentTime}
                    onChange={handleChange}
                  >
                    {timeList.map((time, index) => {
                      const { FutureFulfillmentTime, FutureFulfillmentServiceGap } = dataByTypename(time)
                      if (FutureFulfillmentServiceGap) {
                        return (
                          <option
                            key={index}
                            disabled
                          >----------</option>
                        )
                      }
                      if (FutureFulfillmentTime) {
                        return (
                          <option
                            data-testid='fulfillment-selector-time'
                            value={FutureFulfillmentTime.time}
                            key={time.time}
                          >{formatTime(FutureFulfillmentTime.time)}</option>
                        )
                      }
                    })}
                  </select>
                </div>
              }
            </div>
          </WrappedModal>
        )
      }}
    </Formik>
  )
}

FulfillmentSelectorModalInternal.propTypes = {
  onClose: PropTypes.func.isRequired,
  minimumTakeoutTime: PropTypes.number.isRequired
}

const FulfillmentSelectorModal = ({...props}) => (
  <React.Fragment>
    <FlagOn name='oo-v3-delivery'>
      <DeliveryFulfillmentSelectorModal {...props} />
    </FlagOn>
    <FlagOff name='oo-v3-delivery'>
      <FulfillmentSelectorModalInternal {...props} />
    </FlagOff>
  </React.Fragment>
)

export default FulfillmentSelectorModal
